// Payment method: the demo card on file (PUT/GET/DELETE /me/payment-method).
// Demo only: the API takes published test card numbers and refuses real ones,
// and we only ever see the brand and last four digits back.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, ShieldCheck } from 'lucide-react';
import { ApiError, ok } from '@cover/core/api/client.ts';
import type { PaymentMethod } from '@cover/core/api/types.ts';
import { Badge } from '@cover/core/ui/Badge.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { Field, Input } from '@cover/core/ui/Form.tsx';
import { Load } from '@cover/core/ui/States.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { api, keys, usePaymentMethod } from '../api.ts';
import { PageTitle } from '../Layout.tsx';

const BRAND: Record<PaymentMethod['brand'], string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover' };

/** "Visa ending 4242". */
export function cardLabel(c: Pick<PaymentMethod, 'brand' | 'last4'>) {
  return `${BRAND[c.brand] ?? c.brand} ending ${c.last4}`;
}

/** Only same-site paths, so ?next= can't send anyone elsewhere. */
function safeNext(v: string | null) {
  return v && v.startsWith('/') && !v.startsWith('//') ? v : null;
}

export function PaymentPage() {
  const card = usePaymentMethod();
  const [editing, setEditing] = useState(false);
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  return (
    <>
      <PageTitle lead="The card we use when you buy cover. This is a demo: only test cards work, and no money ever moves.">Payment method</PageTitle>
      <div className="max-w-xl space-y-5">
        <Load q={card}>
          {(c) =>
            c && !editing ? (
              <CardOnFile card={c} onReplace={() => setEditing(true)} next={next} />
            ) : (
              <CardForm hasCard={!!c} onDone={() => setEditing(false)} next={next} />
            )
          }
        </Load>
      </div>
    </>
  );
}

function CardOnFile({ card, onReplace, next }: { card: PaymentMethod; onReplace: () => void; next: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const remove = useMutation({
    mutationFn: () => ok(api.DELETE('/me/payment-method')),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: keys.payment }), qc.invalidateQueries({ queryKey: keys.next })]);
      toast.ok('Card removed.');
    },
    onError: (e) => toast.fail(e),
  });
  return (
    <Card>
      <CardBody className="flex flex-wrap items-center gap-4">
        <span className="flex h-12 w-16 items-center justify-center rounded-lg bg-ink text-white" aria-hidden>
          <CreditCard className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{cardLabel(card)}</p>
          <p className="text-sm text-mid">
            Expires {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
            {card.name && ` · ${card.name}`}
          </p>
        </div>
        <Badge tone="info">Test card</Badge>
        <div className="flex w-full flex-wrap gap-2">
          {next && (
            <Button asChild>
              <Link to={next} className="text-on-brand no-underline hover:no-underline">
                Carry on
              </Link>
            </Button>
          )}
          <Button variant="secondary" onClick={onReplace}>
            Replace card
          </Button>
          <Button variant="ghost" busy={remove.isPending} onClick={() => remove.mutate()}>
            Remove
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

const TEST = { cardNumber: '4242 4242 4242 4242', expiry: '12/30', cvc: '123' };

function CardForm({ hasCard, onDone, next }: { hasCard: boolean; onDone: () => void; next: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState({ cardNumber: '', expiry: '', cvc: '', name: '', postcode: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof f, string>>>({});
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));

  const save = useMutation({
    mutationFn: () =>
      ok(
        api.PUT('/me/payment-method', {
          body: { cardNumber: f.cardNumber.replace(/\s/g, ''), expiry: f.expiry.trim(), cvc: f.cvc.trim(), name: f.name.trim() || undefined, postcode: f.postcode.trim() || undefined },
        }),
      ),
    onSuccess: async (c) => {
      await Promise.all([qc.invalidateQueries({ queryKey: keys.payment }), qc.invalidateQueries({ queryKey: keys.next })]);
      toast.ok(`${cardLabel(c)} saved.`);
      onDone();
      if (next) navigate(next);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'real_card_rejected') setErrors({ cardNumber: 'That isn’t a test card. This is a demo, so use a test card such as 4242 4242 4242 4242.' });
      else if (e instanceof ApiError && e.code === 'card_expired') setErrors({ expiry: 'That card has expired.' });
      else toast.fail(e);
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const errs: typeof errors = {};
    if (f.cardNumber.replace(/\D/g, '').length < 13) errs.cardNumber = 'Enter the card number.';
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(f.expiry.trim())) errs.expiry = 'Use MM/YY, for example 12/30.';
    if (!/^\d{3,4}$/.test(f.cvc.trim())) errs.cvc = 'The 3 or 4 digits on the back.';
    setErrors(errs);
    if (Object.keys(errs).length) {
      const form = e.currentTarget as HTMLFormElement;
      setTimeout(() => form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }
    save.mutate();
  }

  return (
    <Card>
      <CardHeader title={hasCard ? 'Replace your card' : 'Add a card'} />
      <CardBody>
        <div className="mb-5 flex gap-3 rounded-card bg-info-bg/60 p-3 text-sm">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-info-fg" aria-hidden />
          <div>
            <p>
              <strong>Demo only.</strong> Real cards are refused. Use a test card such as 4242 4242 4242 4242, any future expiry and any 3 digits.
            </p>
            <button type="button" className="mt-1.5 min-h-11 font-semibold text-info-fg underline" onClick={() => setF((x) => ({ ...x, ...TEST }))}>
              Fill in a test card
            </button>
          </div>
        </div>
        <form className="space-y-4" onSubmit={submit} noValidate>
          <Field label="Card number" error={errors.cardNumber}>
            <Input inputMode="numeric" autoComplete="off" value={f.cardNumber} onChange={(e) => set('cardNumber', e.target.value)} placeholder="4242 4242 4242 4242" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Expiry" hint="MM/YY" error={errors.expiry}>
              <Input inputMode="numeric" autoComplete="off" value={f.expiry} onChange={(e) => set('expiry', e.target.value)} placeholder="12/30" />
            </Field>
            <Field label="Security code" hint="CVC" error={errors.cvc}>
              <Input inputMode="numeric" autoComplete="off" value={f.cvc} onChange={(e) => set('cvc', e.target.value)} placeholder="123" />
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name on card (optional)">
              <Input autoComplete="off" value={f.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label="Postcode (optional)">
              <Input autoComplete="off" value={f.postcode} onChange={(e) => set('postcode', e.target.value)} />
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" busy={save.isPending}>
              Save card
            </Button>
            {hasCard && (
              <Button type="button" variant="secondary" onClick={onDone}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
